/**
 * The words live mode hears as a stop, by language, and the fold every
 * phrase is matched in (docs/plans/os-voice-conversation.md, 1.2). Shared so
 * the server's stop (`apps/server/src/agent/live/stop-words.ts`, which
 * re-exports these and keeps `isStopSaid`/`isNoSaid`) and the client's pause
 * rule (`live-turn-rules.ts`) read the same lists.
 *
 * Every phrase is a whole utterance, so a sentence that only contains one
 * ("stop the sale") is a question, not a stop. The lists are read together,
 * whatever the call's language: a person speaks what they like to the voice,
 * and there is no spoken-language field to narrow them by.
 */

/**
 * Whole utterances that only ever mean "stop", by language: one list for
 * each of the eleven the app speaks, Hinglish in `hi`. `hi` also holds the
 * English stops as a Hindi-hinted transcriber writes them, in Devanagari;
 * nukta is not folded, so a word written both with and without one is listed
 * both ways. Every list but `en` and `hi` is a draft for Devendra's ear, as
 * the handover lists them; a spelling a transcriber writes differently joins
 * its list.
 */
export const ONLY_STOPS: Readonly<Record<string, readonly string[]>> = {
  en: ['stop', 'stop it', 'stop that', 'stop now', 'never mind', 'nevermind'],
  hi: [
    'रुको',
    'रुकिए',
    'रुक जाओ',
    'बस',
    'बस करो',
    'ruko',
    'rukiye',
    'ruk jao',
    'bas',
    'bas karo',
    // English stops, as a Hindi-hinted transcriber writes them.
    'स्टॉप',
    'स्टॉप इट',
    'स्टॉप करो',
    'नेवर माइंड',
    'नेवरमाइंड',
  ],
  // The other eight languages the app speaks: drafts for Devendra's ear
  // (docs/plans/done/voice-and-coach-handover.md), each already written as
  // `normalise` leaves it. "It's okay" is left out of each (பரவாயில்லை,
  // పర్లేదు, ಪರವಾಗಿಲ್ಲ, സാരമില്ല): said while Coach works it means "no hurry".
  mr: ['थांब', 'थांबा', 'थांबव', 'थांबवा', 'बस', 'बस कर', 'बस करा', 'thamb', 'thamba', 'bas'],
  gu: ['રોકો', 'રોકાઈ જાઓ', 'અટકો', 'બસ', 'બસ કરો', 'roko', 'bas'],
  pa: ['ਰੁਕੋ', 'ਰੁਕ ਜਾਓ', 'ਬੱਸ', 'ਬੱਸ ਕਰੋ', 'ruko', 'bas'],
  bn: ['থামো', 'থামুন', 'আর না', 'বন্ধ করো', 'বন্ধ করুন'],
  ta: ['நிறுத்து', 'நிறுத்துங்கள்', 'போதும்'],
  te: ['ఆపు', 'ఆపండి', 'చాలు'],
  kn: ['ನಿಲ್ಲಿಸು', 'ನಿಲ್ಲಿಸಿ', 'ಸಾಕು'],
  ml: ['നിർത്ത്', 'നിർത്തൂ', 'മതി'],
};

/**
 * Whole utterances that stop Coach and are also a plain "no" ("cancel it",
 * "leave it", छोड़ो, வேண்டாம்), by language, written as `ONLY_STOPS` is.
 * Said to a card the voice read back ("Save it?"), one is that card's answer
 * (rule 4a), so the call stops nothing on one while a card drawn in the call
 * waits and the voice is free; while the voice's own call waits on the running
 * question, it stops that question, since the voice cannot answer the card.
 */
export const NO_PHRASES: Readonly<Record<string, readonly string[]>> = {
  en: ['cancel', 'cancel it', 'cancel that', 'forget it', 'leave it'],
  hi: [
    'रहने दो',
    'छोड़ो',
    'छोड़ दो',
    'rehne do',
    'rahne do',
    'chhodo',
    'chhod do',
    // English ones, as a Hindi-hinted transcriber writes them.
    'कैंसल',
    'कैंसल करो',
    'कैंसिल',
    'कैंसिल करो',
    'फ़ॉरगेट इट',
    'फॉरगेट इट',
    'लीव इट',
  ],
  mr: ['राहू दे', 'सोड', 'सोडा', 'सोडून दे', 'जाऊ दे', 'rahu de', 'sod', 'sodun de'],
  gu: ['રહેવા દો', 'છોડો', 'છોડી દો', 'જવા દો', 'rehva do', 'chhodo'],
  pa: ['ਰਹਿਣ ਦਿਓ', 'ਛੱਡੋ', 'ਛੱਡ ਦਿਓ', 'ਜਾਣ ਦਿਓ', 'rehan dio', 'chhaddo'],
  bn: ['থাক', 'বাদ দাও', 'বাদ দিন', 'ছেড়ে দাও', 'ছেড়ে দিন'],
  ta: ['வேண்டாம்', 'விடு', 'விடுங்கள்'],
  te: ['వద్దు', 'వదిలేయ్', 'వదిలేయండి'],
  kn: ['ಬೇಡ', 'ಬಿಡು', 'ಬಿಡಿ'],
  ml: ['വേണ്ട', 'വിട്', 'വിട്ടേക്ക്'],
};

/** Every stop, by language: the ones that only mean stop, then the ones that are also a "no". */
export const STOP_PHRASES: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  Object.entries(ONLY_STOPS).map(([tag, list]) => [tag, [...list, ...(NO_PHRASES[tag] ?? [])]]),
);

/** One leading word a person puts before a stop, and one trailing one. */
export const SAID_LEADING: ReadonlySet<string> = new Set(['ok', 'okay', 'please', 'arre', 'अरे']);
export const SAID_TRAILING: ReadonlySet<string> = new Set(['please', 'प्लीज़']);

export const phraseSetOf = (lists: Readonly<Record<string, readonly string[]>>): ReadonlySet<string> =>
  new Set(Object.values(lists).flatMap((list) => list.map((one) => one.normalize('NFC'))));

/** What was said, folded as every phrase is: NFC, lower case, no punctuation, one space between words. */
export const normaliseSaid = (text: string): string =>
  text
    .normalize('NFC')
    .toLowerCase()
    .replace(/[.,!?।…"']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** What was said, written as a phrase is, without one "ok" before it or one "please" after. */
export const saidPhraseOf = (text: string): string => {
  const words = normaliseSaid(text).split(' ').filter((one) => one !== '');
  if (words.length > 1 && SAID_LEADING.has(words[0] ?? '')) words.shift();
  if (words.length > 1 && SAID_TRAILING.has(words.at(-1) ?? '')) words.pop();
  return words.join(' ');
};

/**
 * Whole utterances that are a plain "yes" or "no" said to OS, written as the
 * stops are: a short answer while OS speaks pauses it, as a stop does
 * (contract rule 7). `en` and `hi` (Hinglish too) only; drafts for
 * Devendra's ear, as the stops are (J39).
 */
export const YES_NO_PHRASES: Readonly<Record<string, readonly string[]>> = {
  en: ['yes', 'yeah', 'yep', 'no', 'nope', 'ok', 'okay', 'sure'],
  hi: ['हाँ', 'हां', 'हा', 'नहीं', 'नही', 'ना', 'ठीक है', 'haan', 'han', 'haa', 'nahi', 'nahin', 'na', 'theek hai', 'thik hai', 'acha', 'accha'],
};
